<template>
    <NavbarLoggedRoot />

    <ClientOnly>
        <NpsBanner />
        <LegalAgreementAcceptanceDialog
            v-if="
                $ff('compliance-legal-agreements') &&
                user.selectedIdentity?.umbrellaCommercialContractAcceptance?.required &&
                user.selectedIdentity.umbrellaCommercialContractAcceptance.versionId
            "
            :key="`${user.selectedIdentity.id}-${user.selectedIdentity.companyId}`"
            :company-id="user.selectedIdentity.companyId"
            :version-id="user.selectedIdentity.umbrellaCommercialContractAcceptance.versionId"
        />
    </ClientOnly>
</template>

<script setup lang="ts">
    import {useHeaderMenuStore} from '@navbar-logged/features/header-menu';
    import {storeToRefs} from 'pinia';
    import {nextTick, onMounted, useHead} from '#imports';
    import {useDisplayMobile} from '@malt/utils';
    import {useDisplayModeStore} from './features/aside-menu';
    import {useLanguageSelectorStore} from './features/language-selector';
    import LegalAgreementAcceptanceDialog from './features/legal-agreement-acceptance/LegalAgreementAcceptanceDialog.vue';
    import NpsBanner from './features/nps/ui/NpsBanner.vue';
    import {useSearchDisplayMode} from './features/search';
    import {useUserStore} from './features/user';
    import NavbarLoggedRoot from './NavbarLoggedRoot.vue';

    const displayModeStore = useDisplayModeStore();
    const user = useUserStore();
    const {displayMode, isDisplayModeFunnel} = storeToRefs(displayModeStore);
    const {isDisplayMobile} = useDisplayMobile();
    const languageSelectorStore = useLanguageSelectorStore();
    const {searchDisplayMode} = useSearchDisplayMode();

    useHead({
        htmlAttrs: {
            'data-display-mode': displayMode,
            'data-display-funnel': isDisplayModeFunnel,
            'data-display-mobile': isDisplayMobile,
            'data-search-mode': searchDisplayMode,
            'data-user-type': user.selectedIdentity?.type,
            lang: languageSelectorStore.htmlLangAttribute,
            'data-nuxt-app': '',
        },
    });

    const {onClientOnboardingIsPresent} = useHeaderMenuStore();

    onMounted(() => {
        // Handle useHead bug where html attrs are not set if redirected from another page
        if (!document.documentElement.getAttributeNames().includes('data-nuxt-app')) {
            document.documentElement.setAttribute('data-display-mode', displayMode.value);
            if (isDisplayModeFunnel.value) {
                document.documentElement.setAttribute('data-display-funnel', '');
            }
            if (isDisplayMobile.value) {
                document.documentElement.setAttribute('data-display-mobile', '');
            }
            document.documentElement.setAttribute('data-search-mode', searchDisplayMode.value);
            document.documentElement.setAttribute('data-user-type', String(user.selectedIdentity?.type));
            document.documentElement.setAttribute('lang', languageSelectorStore.htmlLangAttribute);
            document.documentElement.setAttribute('data-nuxt-app', '');
        }

        if (import.meta.client) {
            nextTick().then(() => {
                if (document.getElementById('client-onboarding-wrapper')) {
                    onClientOnboardingIsPresent();
                }
            });
        }
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    @include mq.screen_sm(min) {
        :global(html[data-display-mode='REDUCED']) {
            :root {
                --aside-menu-width: 72px;
                --aside-menu-left: 0px;
                --header-menu-height: 70px;
                --header-menu-item-margin-top: 0px;
                --layout-padding-left: 72px;
                --layout-padding-top: 70px;
                --header-height: 70px !important;
            }
        }

        :global(html[data-display-mode='FULL']) {
            :root {
                --aside-menu-width: 264px;
                --aside-menu-left: 0px;
                --header-menu-height: 70px;
                --header-menu-item-margin-top: 0px;
                --layout-padding-left: 264px;
                --layout-padding-top: 70px;
                --header-height: 70px !important;
            }
        }
    }

    @include mq.screen_sm {
        :global(html[data-user-type='CLIENT']) {
            :root {
                --aside-menu-width: 264px;
                --aside-menu-left: -100%;
                --header-menu-item-margin-top: 0px;
                --layout-padding-left: 0px;
            }
        }

        :global(html[data-user-type='FREELANCE']),
        :global(html[data-user-type='SUBCONTRACTOR']) {
            :root {
                --aside-menu-width: 264px;
                --aside-menu-left: -100%;
                --header-menu-height: 70px;
                --header-menu-item-margin-top: 0px;
                --layout-padding-left: 0px;
                --layout-padding-top: 70px;
                --header-height: 70px !important;
            }
        }

        :global(html[data-search-mode='FORM'][data-user-type='CLIENT']) {
            --header-menu-height: 145px;
            --layout-padding-top: 145px;
            --header-height: 145px !important;
        }
    }

    :global(body) {
        padding-left: var(--layout-padding-left);
        padding-top: var(--layout-padding-top);
        max-width: none !important;

        @include mq.screen_sm {
            padding-left: 0;
        }
    }

    :global(html[data-display-funnel='true']) {
        :root {
            --layout-padding-left: 0px !important;
            --header-menu-height: 70px !important;
            --layout-padding-top: 70px;
        }
    }

    :global(.navbar-app) {
        font-size: var(--joy-font-size-primary-400);
        font-family: var(--joy-font-family-base);
    }

    :global(*.flex) {
        display: flex;
        flex-flow: row wrap;
    }

    :global(*.grid) {
        display: grid;
    }

    :global(*.flex),
    :global(*.grid) {
        max-width: 100%;
        min-width: 0;
        position: relative;

        & > * {
            max-width: 100%;
            min-width: 0;
            position: relative;
        }
    }

    :global(*) {
        box-sizing: border-box;
    }

    :global(button) {
        border: none;
        appearance: none;
        background: none;
        margin: 0;
        padding: 0;
    }
</style>
